<template>
    <div v-if="model">
        <QBtn
            color="primary"
            noCaps
            outline
            icon="visibility"
            :label="$t('projects.models.preview')"
            class="full-width q-mb-md"
            @click="$emit('preview')"
        />

        <q-expansion-item
            icon="insights"
            :label="$t('projects.models.drawer.summaryTitle')"
            defaultOpened
            class="ContextDrawerExpansion ContextDrawerExpansion--last"
            headerClass="ContextDrawerExpansionHeader"
        >
            <div class="ContextDrawerExpansionContent">
                <div
                    v-if="summaryError"
                    class="text-negative text-caption q-mb-sm"
                >
                    {{ summaryError }}
                </div>

                <div
                    v-if="processName"
                    class="InfoRow q-mb-sm"
                >
                    <div class="InfoRowLabel text-caption">
                        {{ $t('general.name') }}
                    </div>
                    <div class="InfoRowValue text-body2 text-primary">
                        {{ processName }}
                    </div>
                </div>

                <div
                    v-for="row in summaryRows"
                    :key="row.key"
                    class="InfoRow q-mb-sm"
                >
                    <div class="InfoRowLabel text-caption">
                        {{ row.label }}
                    </div>
                    <div class="InfoRowValue text-body2 text-primary">
                        {{ row.value }}
                    </div>
                </div>
            </div>
        </q-expansion-item>
    </div>
</template>

<script>
import VueTypes from 'vue-types'

export default {
    name: 'ProjectModelInfoDrawerPanel',
    props: {
        model: VueTypes.any,
    },
    emits: ['preview'],
    data() {
        return {
            processName: '',
            summary: null,
            summaryError: '',
        }
    },
    computed: {
        summaryRows() {
            const s = this.summary || {}
            return [
                { key: 'tasks', label: this.$t('projects.models.drawer.tasks'), value: s.tasks ?? '-' },
                { key: 'events', label: this.$t('projects.models.drawer.events'), value: s.events ?? '-' },
                { key: 'gateways', label: this.$t('projects.models.drawer.gateways'), value: s.gateways ?? '-' },
                { key: 'flows', label: this.$t('projects.models.drawer.flows'), value: s.flows ?? '-' },
                { key: 'pools', label: this.$t('projects.models.drawer.pools'), value: s.pools ?? '-' },
                { key: 'lanes', label: this.$t('projects.models.drawer.lanes'), value: s.lanes ?? '-' },
                { key: 'subprocesses', label: this.$t('projects.models.drawer.subprocesses'), value: s.subprocesses ?? '-' },
            ]
        },
    },
    watch: {
        model: {
            handler() {
                this.extractSummary()
            },
            immediate: true,
        },
    },
    methods: {
        safeLocalName(node) {
            return ((node && (node.localName || node.nodeName)) || '').toString()
        },
        countByPredicate(nodes, predicate) {
            let count = 0
            nodes.forEach((n) => {
                if (predicate(n)) count += 1
            })
            return count
        },
        extractSummary() {
            this.summary = null
            this.summaryError = ''
            this.processName = ''

            const xml = this.model?.bpmn
            if (!xml) return

            try {
                const doc = new DOMParser().parseFromString(xml, 'text/xml')
                const parseError = doc.getElementsByTagName('parsererror')
                if (parseError && parseError.length) {
                    this.summaryError = this.$t('projects.models.drawer.parseError')
                    return
                }

                const all = Array.from(doc.getElementsByTagName('*'))

                const local = (n) => this.safeLocalName(n)
                const localLower = (n) => local(n).toLowerCase()

                const processEl = all.find((n) => this.safeLocalName(n) === 'process')
                const processName = processEl?.getAttribute?.('name')
                if (processName) this.processName = processName

                const tasks = this.countByPredicate(all, (n) => {
                    const t = localLower(n)
                    return t === 'task' || t.endsWith('task')
                })
                const events = this.countByPredicate(all, (n) => localLower(n).endsWith('event'))
                const gateways = this.countByPredicate(all, (n) => localLower(n).endsWith('gateway'))
                const flows = this.countByPredicate(all, (n) => localLower(n) === 'sequenceflow')
                const pools = this.countByPredicate(all, (n) => localLower(n) === 'participant')
                const lanes = this.countByPredicate(all, (n) => localLower(n) === 'lane')
                const subprocesses = this.countByPredicate(all, (n) => localLower(n) === 'subprocess')

                this.summary = {
                    tasks,
                    events,
                    gateways,
                    flows,
                    pools,
                    lanes,
                    subprocesses,
                }
            } catch (e) {
                this.summaryError = this.$t('projects.models.drawer.parseError')
            }
        },
    },
}
</script>

<style scoped lang="scss">
:deep(.ContextDrawerExpansion) {
    border-top: 1px solid $gray-04;
}

:deep(.ContextDrawerExpansion.ContextDrawerExpansion--last) {
    border-bottom: 1px solid $gray-04;
}

:deep(.ContextDrawerExpansionHeader .q-item__label) {
    font-weight: 700;
}

:deep(.ContextDrawerExpansionHeader .q-item__section--avatar .q-icon) {
    color: $primary;
}

.ContextDrawerExpansionContent {
    padding: 8px 16px 16px;
}

.InfoRow {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
}

.InfoRowLabel {
    flex: 0 1 auto;
}

.InfoRowValue {
    flex: 1 1 auto;
    min-width: 0;
    text-align: right;
    word-break: break-word;
}
</style>
